import { CircleAlertIcon as IconAlertCircle } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';

import CreateInquiryWizard from 'dashboard/components/CreateInquiry/CreateInquiryWizard';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Banner from 'dashboard/components-lib/Banner';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { AccountIdPropertyFormData } from 'dashboard/forms/FormAccountIdProperty';
import { FormUpdateAccountNewData } from 'dashboard/forms/FormUpdateAccountNew';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Backdrop, HeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { Account } from 'dashboard/models/account';
import { AccountAction } from 'dashboard/models/account-action';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType, AccountTypesFilterOption } from 'dashboard/models/account-type';
import { AccountTypeGridLayout } from 'dashboard/models/account-type-grid-layouts';
import { AccountVariableDefinition } from 'dashboard/models/account-variable-definition';
import { Case } from 'dashboard/models/case';
import { CaseTemplateBasicsOption, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Device } from 'dashboard/models/device';
import { DocumentGeneric } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Organization } from 'dashboard/models/organization';
import { Report } from 'dashboard/models/report';
import { ReportTemplateFilterOption } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { TriggerAccount } from 'dashboard/models/trigger';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { AccountState, saveAccountInquiries } from 'dashboard/redux/slices/account';
import { AccountDetailsNew } from 'dashboard/views/DashboardAccountsShowView/AccountDetailsNew';
import { AccountDetailsOld } from 'dashboard/views/DashboardAccountsShowView/AccountDetailsOld';
import AccountCreateCaseModal, {
  CreateCaseFormData,
} from 'dashboard/views/DashboardAccountsShowView/components/AccountCreateCaseModal';
import AccountCreateReportModal from 'dashboard/views/DashboardAccountsShowView/components/AccountCreateReportModal';
import AccountHeader from 'dashboard/views/DashboardAccountsShowView/components/AccountHeader';
import EditAccountModalNew from 'dashboard/views/DashboardAccountsShowView/components/EditAccountModalNew';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { FieldsMap } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Json } from 'lib/types';
import { isCreateEnabledTemplateBasic } from 'lib/utils/template';

export interface AccountApi {
  account: Account;
  accountType: AccountType;
  currentEnvironment: Environment;
  relations: AccountState['relations'];
  accountVariableDefinitions: AccountVariableDefinition[];
  tags: Tag[];
  users: User[];
  lists: List[];
  listItems: ListItem[];
  listMatches: ListMatch[];
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
}

export interface DashboardAccountsShowViewProps {
  account: Account;
  accountType: AccountType;
  accountTypes: AccountType[];
  accountStatuses: AccountStatus[];
  accountTags: Tag[];
  accountTypeGridLayouts: AccountTypeGridLayout[];
  accountVariableDefinitions: AccountVariableDefinition[];
  accountTypesFilterOption: AccountTypesFilterOption;
  accountActions: AccountAction[];
  actionWorkflowVersions: WorkflowVersion[];
  triggers: TriggerAccount[];
  inquiries?: Inquiry[];
  inquiryTemplateFilterOptions?: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  creatableReportTemplateFilterOptions: ReportTemplateFilterOption[];
  themes?: Theme[];
  devices?: Device[];
  selfies?: Selfie[];
  documents?: DocumentGeneric[];
  cases?: Case[];
  caseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  graph: Graph;
  graphQueryTemplateId: string;
  graphQueryTemplates: GraphQueryTemplate[];
  redactor?: User;
  reports: Report[];
  currentEnvironment: Environment;
  currentUser: User;
  users: User[];
  organization: Organization;
  isCreateReportButtonDisabled: boolean;
  isLoadingAccount: boolean;
  isLoadingAccountActions: boolean;
  isLoadingDocuments: boolean;
  isLoadingInquiries: boolean;
  isLoadingGraph: boolean;
  isLoadingReports: boolean;
  isLoadingReportTemplates: boolean;
  isLoadingVerifications: boolean;
  featureFlags: FeatureFlagsMap;
  nullStateMessage: string;
  documentPaginationParams: PaginationParams;
  inquiryPaginationParams: PaginationParams;
  verificationPaginationParams: PaginationParams;
  reportPaginationParams: PaginationParams;
  documentPageSize: number;
  inquiryPageSize: number;
  verificationPageSize: number;
  reportPageSize: number;
  relations: AccountState['relations'];
  verifications: Verification[];
  lists: List[];
  listItems: ListItem[];
  listMatches: ListMatch[];
  handleDocumentPageLinkClick: (pageNumber: number) => void;
  handleDocumentPageSizeClick: (pageSize: number) => void;
  handleInquiryPageLinkClick: (pageNumber: number) => void;
  handleInquiryPageSizeClick: (pageSize: number) => void;
  handleVerificationPageLinkClick: (pageNumber: number) => void;
  handleVerificationPageSizeClick: (pageSize: number) => void;
  handleReportPageLinkClick: (pageNumber: number) => void;
  handleReportPageSizeClick: (pageSize: number) => void;
  handleUpdateAccount: (values: FormUpdateAccountNewData) => Promise<void>;
  handleCreateIdNumber: (values: {
    'identification-number': string;
    'identification-class': string;
    'country-code': string;
  }) => Promise<void>;
  handleUpdateAccountStatus: (accountStatusId: string) => Promise<void>;
  handleUpdateAccountIdNumbers: (values: AccountIdPropertyFormData, index: number) => Promise<void>;
  handleUpdateAccountMainSelfie: (selfieId: string) => Promise<void>;
  handleCreateReport: (reportTemplateFilterOption: ReportTemplateFilterOption) => Promise<void>;
  handleCreateCase: (values: CreateCaseFormData) => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => Promise<void>;
  handleAddTags: (accountIds: string[], tags: FormUpdateTagsData) => Promise<void>;
  handleBulkUpdateAccountStatus: (accountIds: string[], accountStatusId: string) => Promise<void>;
  handleRemoveDevice: (deviceId: string) => Promise<void>;
  handleRunAccountAction: (values: Json, accountActionId: string) => Promise<void>;
  redactAccount: (accountId: string) => Promise<void>;
}

const DashboardAccountsShowView = ({
  account,
  accountType,
  accountTypes,
  accountStatuses,
  accountTags,
  accountTypeGridLayouts,
  accountVariableDefinitions,
  accountTypesFilterOption,
  accountActions,
  actionWorkflowVersions,
  triggers,
  currentEnvironment,
  currentUser,
  users,
  isCreateReportButtonDisabled,
  isLoadingAccount,
  isLoadingAccountActions,
  isLoadingDocuments,
  isLoadingInquiries,
  isLoadingReports,
  isLoadingReportTemplates,
  isLoadingVerifications,
  organization,
  featureFlags,
  redactor,
  devices = [],
  selfies = [],
  documents = [],
  cases = [],
  caseTemplateBasicsOptions = [],
  graph,
  graphQueryTemplateId,
  graphQueryTemplates,
  inquiries = [],
  inquiryTemplateFilterOptions = [],
  themes = [],
  relations,
  reports = [],
  verifications = [],
  creatableReportTemplateFilterOptions,
  nullStateMessage,
  documentPaginationParams,
  inquiryPaginationParams,
  verificationPaginationParams,
  reportPaginationParams,
  reportPageSize,
  documentPageSize,
  inquiryPageSize,
  verificationPageSize,
  lists,
  listItems,
  listMatches,
  handleUpdateAccount,
  handleCreateIdNumber,
  handleCreateReport,
  handleCreateCase,
  handleUpdateTags,
  handleUpdateAccountIdNumbers,
  handleUpdateAccountStatus,
  handleUpdateAccountMainSelfie,
  handleRemoveDevice,
  handleRunAccountAction,
  redactAccount,
  handleDocumentPageLinkClick,
  handleDocumentPageSizeClick,
  handleInquiryPageLinkClick,
  handleInquiryPageSizeClick,
  handleVerificationPageLinkClick,
  handleVerificationPageSizeClick,
  handleReportPageLinkClick,
  handleReportPageSizeClick,
}: DashboardAccountsShowViewProps) => {
  const [
    showCreateInquiryWizard,
    { enable: openCreateInquiryWizard, disable: hideCreateInquiryWizard },
  ] = useToggle();
  const [updateAccountModal, { enable: showUpdateAccountModal, disable: hideUpdateAccountModal }] =
    useToggle(false);
  const [redactAccountModal, { enable: showRedactAccountModal, disable: hideRedactAccountModal }] =
    useToggle(false);
  const [createReportModal, { enable: showCreateReportModal, disable: hideCreateReportModal }] =
    useToggle(false);
  const [createCaseModal, { enable: showCreateCaseModal, disable: hideCreateCaseModal }] =
    useToggle(false);
  const dispatch = useDispatch();

  const defaultGridLayout = accountTypeGridLayouts[0];
  // Only show grid layout if the feature flag is enabled and there is a grid layout available. Otherwise, default to old accounts
  const isAccountGridLayoutEnabled =
    featureFlags['dashboard:accounts-show-grid-layout'] && defaultGridLayout != null;

  const visibleActionIds = defaultGridLayout?.attributes.configuration['visible-action-ids'];

  const createEnabledInquiryTemplateFilterOptions = inquiryTemplateFilterOptions.filter(
    isCreateEnabledTemplateBasic,
  );
  const initialTemplateFilterOption = createEnabledInquiryTemplateFilterOptions[0];

  // Eventual consistency of the inquiry index endpoint means we need to append it here to have it show up
  // immediately in the table post-create
  const addCreatedInquiryToList = useCallback(
    (createdInquiry: Inquiry) => {
      dispatch(
        saveAccountInquiries({
          inquiries: [createdInquiry, ...inquiries],
          paginationParams: inquiryPaginationParams,
        }),
      );
    },
    [inquiries, inquiryPaginationParams],
  );

  if (account == null || isLoadingAccount) {
    return isLoadingAccount ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState message={nullStateMessage} backRoute='/dashboard/accounts' />
      </Backdrop>
    );
  }

  const handleUpdateAccountAndCloseNew = async (values: FieldsMap) => {
    const formValues: FormUpdateAccountNewData = { fields: values };
    await handleUpdateAccount(formValues);
    hideUpdateAccountModal();
  };

  const handleCreateCaseAndClose = async (values: CreateCaseFormData) => {
    await handleCreateCase(values);
    hideCreateCaseModal();
  };

  const confirmRedactAccount = () => redactAccount(account.id);
  const redactorName =
    redactor == null
      ? 'via API'
      : `by ${redactor.attributes['name-first']} ${redactor.attributes['name-last']}`;
  let hasBeenRedacted = false;
  let redactionBannerText = null;
  if (account.attributes['redacted-at']) {
    hasBeenRedacted = true;
    if (account.attributes['marked-for-cascading-redaction-at']) {
      redactionBannerText = `This account was marked for redaction ${redactorName} on ${DateTime.fromISO(
        account.attributes['marked-for-cascading-redaction-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`;
    } else {
      redactionBannerText = `This account was last redacted ${redactorName} on ${DateTime.fromISO(
        account.attributes['redacted-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`;
    }
  }

  const enabledCreatableReportTemplateFilterOptions = creatableReportTemplateFilterOptions.filter(
    (t) =>
      t.type !== ApiModel.ReportTemplateProfile ||
      featureFlags['dashboard:enable-create-reports-profile-reports'],
  );

  const accountApi = {
    account,
    accountType,
    currentEnvironment,
    relations,
    accountVariableDefinitions,
    tags: accountTags,
    users: users,
    handleUpdateTags,
    lists,
    listItems,
    listMatches,
  };

  return (
    <>
      <Helmet title={`${account.id} - Accounts`} />

      {redactionBannerText && (
        <Banner data-test='account-redaction-banner' icon={<IconAlertCircle />}>
          {redactionBannerText}
        </Banner>
      )}

      {showCreateInquiryWizard && (
        <CreateInquiryWizard
          templateFilterOptions={createEnabledInquiryTemplateFilterOptions}
          accountTypeBasics={[]}
          account={account}
          organization={organization}
          currentEnvironment={currentEnvironment}
          themes={themes}
          onInquiryCreate={addCreatedInquiryToList}
          emailAddressPrefill={
            currentEnvironment.type === ApiModel.EnvironmentSandbox
              ? currentUser.attributes['email-address']
              : undefined
          }
          hideCreateInquiryWizard={hideCreateInquiryWizard}
          isOpen={showCreateInquiryWizard}
          initialValues={{
            templateId: initialTemplateFilterOption?.id,
            themeId:
              initialTemplateFilterOption?.type === ApiModel.Template
                ? initialTemplateFilterOption['default-theme-id']
                : undefined,
          }}
        />
      )}

      <ModalWrapper isOpen={updateAccountModal} handleCloseModal={hideUpdateAccountModal}>
        <EditAccountModalNew
          onSubmit={handleUpdateAccountAndCloseNew}
          account={account}
          relations={relations}
          hideModal={hideUpdateAccountModal}
          accountType={accountType}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={redactAccountModal} handleCloseModal={hideRedactAccountModal}>
        <ConfirmationModal
          cancelAction={hideRedactAccountModal}
          confirmAction={confirmRedactAccount}
          header='Redact this account’s PII?'
          confirmActionText='Redact'
        >
          Personally identifiable information will be redacted. This action is permanent and can’t
          be undone.
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={createReportModal} handleCloseModal={hideCreateReportModal}>
        <AccountCreateReportModal
          accountType={accountType}
          reportTemplateFilterOptions={enabledCreatableReportTemplateFilterOptions}
          handleCreateReport={handleCreateReport}
          handleClose={hideCreateReportModal}
          account={account}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={createCaseModal} handleCloseModal={hideCreateCaseModal}>
        <AccountCreateCaseModal
          caseTemplateBasicsOptions={caseTemplateBasicsOptions.filter(
            (caseTemplate) => caseTemplate.status === CaseTemplateStatus.Active,
          )}
          inquiries={inquiries}
          reports={reports}
          submit={handleCreateCaseAndClose}
          handleClose={hideCreateCaseModal}
        />
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      <Backdrop>
        <HeaderContainer>
          <AccountHeader
            currentEnvironment={currentEnvironment}
            showCreateInquiryModal={openCreateInquiryWizard}
            showUpdateAccountModal={showUpdateAccountModal}
            showRedactAccountModal={showRedactAccountModal}
            showCreateReportModal={showCreateReportModal}
            showCreateCaseModal={showCreateCaseModal}
            isCreateReportButtonDisabled={
              isCreateReportButtonDisabled ||
              enabledCreatableReportTemplateFilterOptions.length === 0
            }
            disabledReportReason={
              enabledCreatableReportTemplateFilterOptions.length === 0
                ? 'Accounts must  have sufficient input to create a report '
                : 'Permissions required to enable this feature.'
            }
            isLoadingReportTemplates={isLoadingReportTemplates}
            isLoadingAccountActions={isLoadingAccountActions}
            inquiryTemplateFilterOptions={inquiryTemplateFilterOptions}
            account={account}
            accountStatuses={accountStatuses}
            accountActions={accountActions}
            visibleActionIds={visibleActionIds}
            actionWorkflowVersions={actionWorkflowVersions}
            accountVariableDefinitions={accountVariableDefinitions}
            triggers={triggers}
            featureFlags={featureFlags}
            handleUpdateAccountStatus={handleUpdateAccountStatus}
            handleRunAccountAction={handleRunAccountAction}
          />
        </HeaderContainer>
        {isAccountGridLayoutEnabled ? (
          <AccountDetailsNew
            key={accountApi.account.id}
            configuration={defaultGridLayout.attributes.configuration}
            accountApi={accountApi}
          />
        ) : (
          <AccountDetailsOld
            account={account}
            accountStatuses={accountStatuses}
            accountTags={accountTags}
            accountTypes={accountTypes}
            accountTypesFilterOption={accountTypesFilterOption}
            cases={cases}
            currentEnvironment={currentEnvironment}
            currentUser={currentUser}
            devices={devices}
            documents={documents}
            featureFlags={featureFlags}
            graph={graph}
            graphQueryTemplateId={graphQueryTemplateId}
            graphQueryTemplates={graphQueryTemplates}
            hasBeenRedacted={hasBeenRedacted}
            inquiries={inquiries}
            inquiryTemplateFilterOptions={inquiryTemplateFilterOptions}
            isLoadingAccount={isLoadingAccount}
            isLoadingDocuments={isLoadingDocuments}
            isLoadingInquiries={isLoadingInquiries}
            isLoadingReports={isLoadingReports}
            isLoadingVerifications={isLoadingVerifications}
            documentPageSize={documentPageSize}
            documentPaginationParams={documentPaginationParams}
            inquiryPageSize={inquiryPageSize}
            inquiryPaginationParams={inquiryPaginationParams}
            relations={relations}
            reports={reports}
            selfies={selfies}
            users={users}
            reportPaginationParams={reportPaginationParams}
            reportPageSize={reportPageSize}
            verifications={verifications}
            verificationPaginationParams={verificationPaginationParams}
            verificationPageSize={verificationPageSize}
            lists={lists}
            listItems={listItems}
            listMatches={listMatches}
            handleCreateIdNumber={handleCreateIdNumber}
            handleUpdateTags={handleUpdateTags}
            handleUpdateAccountIdNumbers={handleUpdateAccountIdNumbers}
            handleUpdateAccountMainSelfie={handleUpdateAccountMainSelfie}
            handleReportPageLinkClick={handleReportPageLinkClick}
            handleReportPageSizeClick={handleReportPageSizeClick}
            handleRemoveDevice={handleRemoveDevice}
            handleDocumentPageLinkClick={handleDocumentPageLinkClick}
            handleDocumentPageSizeClick={handleDocumentPageSizeClick}
            handleInquiryPageLinkClick={handleInquiryPageLinkClick}
            handleInquiryPageSizeClick={handleInquiryPageSizeClick}
            handleVerificationPageLinkClick={handleVerificationPageLinkClick}
            handleVerificationPageSizeClick={handleVerificationPageSizeClick}
          />
        )}
      </Backdrop>
    </>
  );
};

export default DashboardAccountsShowView;
